import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Settings, Plus, Pencil, Trash2, Loader2, X, Wallet, ToggleLeft, ToggleRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminRechargeConfigPage = () => {
  const { getAuthHeaders } = useAuth();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    method_name: '', currency_code: '', exchange_rate: '', commission_pct: '0',
    min_amount: '1', max_amount: '99999', instructions: '', is_active: true
  });

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/recharge-config`, { headers: getAuthHeaders() });
      setConfigs(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setForm({ method_name: '', currency_code: '', exchange_rate: '', commission_pct: '0', min_amount: '1', max_amount: '99999', instructions: '', is_active: true });
    setEditing(null);
    setShowForm(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      exchange_rate: parseFloat(form.exchange_rate),
      commission_pct: parseFloat(form.commission_pct),
      min_amount: parseFloat(form.min_amount),
      max_amount: parseFloat(form.max_amount),
    };
    try {
      if (editing) {
        await axios.put(`${API}/admin/recharge-config/${editing}`, payload, { headers: getAuthHeaders() });
        toast.success('Configuracion actualizada');
      } else {
        await axios.post(`${API}/admin/recharge-config`, payload, { headers: getAuthHeaders() });
        toast.success('Configuracion creada');
      }
      resetForm();
      load();
    } catch (e) { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar esta configuracion de recarga?')) return;
    try {
      await axios.delete(`${API}/admin/recharge-config/${id}`, { headers: getAuthHeaders() });
      toast.success('Eliminada');
      load();
    } catch (e) { toast.error('Error'); }
  };

  const startEdit = (c) => {
    setForm({
      method_name: c.method_name,
      currency_code: c.currency_code,
      exchange_rate: c.exchange_rate?.toString(),
      commission_pct: c.commission_pct?.toString() || '0',
      min_amount: c.min_amount?.toString() || '1',
      max_amount: c.max_amount?.toString() || '99999',
      instructions: c.instructions || '',
      is_active: c.is_active,
    });
    setEditing(c.id);
    setShowForm(true);
  };

  const toggleActive = async (c) => {
    try {
      await axios.put(`${API}/admin/recharge-config/${c.id}`, { is_active: !c.is_active }, { headers: getAuthHeaders() });
      load();
    } catch (e) { toast.error('Error'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  // Calculator preview
  const exRate = parseFloat(form.exchange_rate) || 0;
  const commPct = parseFloat(form.commission_pct) || 0;
  const exampleUSD = 100;
  const exampleCost = exRate > 0 ? exampleUSD * exRate * (1 + commPct / 100) : 0;

  return (
    <div className="space-y-6" data-testid="admin-recharge-config-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Configuracion de Recargas</h1>
          <p className="text-slate-500">Monedas, tasas y comisiones para recargar wallet</p>
        </div>
        <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-blue-700 hover:bg-blue-800" data-testid="add-config-btn">
          <Plus className="w-4 h-4 mr-2" /> Agregar Metodo
        </Button>
      </div>

      {showForm && (
        <Card className="p-6 border-blue-200 bg-blue-50" data-testid="recharge-config-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">{editing ? 'Editar' : 'Nuevo'} Metodo de Recarga</h3>
            <button onClick={resetForm}><X className="w-5 h-5 text-slate-400" /></button>
          </div>
          <form onSubmit={handleSave} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Nombre del metodo</Label>
              <Input value={form.method_name} onChange={(e) => setForm({ ...form, method_name: e.target.value })} placeholder="Ej: Zelle, Efectivo CUP" required data-testid="config-name" />
            </div>
            <div className="space-y-2">
              <Label>Codigo moneda</Label>
              <Input value={form.currency_code} onChange={(e) => setForm({ ...form, currency_code: e.target.value.toUpperCase() })} placeholder="Ej: USD, CUP, MLC" required data-testid="config-currency" />
            </div>
            <div className="space-y-2">
              <Label>Tasa de cambio (1 USD = X)</Label>
              <Input type="number" step="0.0001" value={form.exchange_rate} onChange={(e) => setForm({ ...form, exchange_rate: e.target.value })} placeholder="Ej: 1 para USD, 350 para CUP" required data-testid="config-rate" />
            </div>
            <div className="space-y-2">
              <Label>Comision admin (%)</Label>
              <Input type="number" step="0.1" value={form.commission_pct} onChange={(e) => setForm({ ...form, commission_pct: e.target.value })} data-testid="config-commission" />
            </div>
            <div className="space-y-2">
              <Label>Monto minimo (USD)</Label>
              <Input type="number" step="0.01" value={form.min_amount} onChange={(e) => setForm({ ...form, min_amount: e.target.value })} data-testid="config-min" />
            </div>
            <div className="space-y-2">
              <Label>Monto maximo (USD)</Label>
              <Input type="number" step="0.01" value={form.max_amount} onChange={(e) => setForm({ ...form, max_amount: e.target.value })} data-testid="config-max" />
            </div>
            <div className="space-y-2 sm:col-span-2 lg:col-span-3">
              <Label>Instrucciones (opcional)</Label>
              <Input value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} placeholder="Instrucciones para el cliente al recargar con este metodo" data-testid="config-instructions" />
            </div>
            {exRate > 0 && (
              <div className="sm:col-span-2 lg:col-span-3 p-3 bg-white rounded-lg border border-green-200">
                <p className="text-sm text-slate-600">Vista previa: Para recargar <strong>$100 USD</strong> en wallet, el cliente pagara:</p>
                <p className="text-lg font-bold text-green-700">{exampleCost.toFixed(2)} {form.currency_code || '???'}</p>
                <p className="text-xs text-slate-400">Tasa: {exRate} | Comision: {commPct}%</p>
              </div>
            )}
            <div className="sm:col-span-2 lg:col-span-3 flex gap-3">
              <Button type="submit" disabled={saving} className="bg-blue-700 hover:bg-blue-800" data-testid="config-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? 'Actualizar' : 'Crear')}
              </Button>
              <Button type="button" variant="outline" onClick={resetForm}>Cancelar</Button>
            </div>
          </form>
        </Card>
      )}

      {configs.length === 0 ? (
        <Card className="p-12 text-center border-0 shadow-sm">
          <Settings className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">No hay metodos de recarga configurados</p>
          <p className="text-xs text-slate-400 mt-1">Agrega los metodos de pago y monedas con los que tus clientes y gestores recargaran su wallet.</p>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {configs.map((c) => {
            const costFor100 = c.exchange_rate * 100 * (1 + (c.commission_pct || 0) / 100);
            return (
              <Card key={c.id} className={`p-4 border-0 shadow-sm ${!c.is_active ? 'opacity-50' : ''}`} data-testid={`config-${c.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Wallet className="w-5 h-5 text-blue-500" />
                    <span className="font-bold text-lg">{c.method_name}</span>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => toggleActive(c)} className={`p-1 ${c.is_active ? 'text-green-500' : 'text-slate-400'}`} title={c.is_active ? 'Desactivar' : 'Activar'} data-testid={`toggle-${c.id}`}>
                      {c.is_active ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                    </button>
                    <button onClick={() => startEdit(c)} className="p-1 text-blue-500" data-testid={`edit-${c.id}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(c.id)} className="p-1 text-red-500" data-testid={`delete-${c.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="space-y-1 text-sm">
                  <p>Moneda: <strong>{c.currency_code}</strong></p>
                  <p>Tasa: <strong>1 USD = {c.exchange_rate} {c.currency_code}</strong></p>
                  <p>Comision: <strong>{c.commission_pct}%</strong></p>
                  <p className="text-green-600 font-semibold">$100 USD = {costFor100.toFixed(2)} {c.currency_code}</p>
                  <p className="text-xs text-slate-400">Min: ${c.min_amount} | Max: ${c.max_amount}</p>
                  {c.instructions && <p className="text-xs text-slate-500 mt-2 italic">{c.instructions}</p>}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminRechargeConfigPage;
